import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	input,
	signal,
	ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import { DynamicModule, InputsType } from 'ng-dynamic-component';
import { map } from 'rxjs';

import { AlertComponent } from '@valk-nx/components/ui-alert/src/lib/alert.component';
import type { FiltersInterface } from '@valk-nx/compositions/ui-filters/src/lib/contracts/filters.interface';
import { FilterHeadingComponent } from '@valk-nx/compositions/ui-filters/src/lib/filter-heading/filter-heading';
import { FiltersComponent } from '@valk-nx/compositions/ui-filters/src/lib/filters.component';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import {
	negativeHeader$,
	PRICING_TOOLTIP_OCCUPANCY,
} from '@valk-nx/core/lib/core';
import { CarouselComponent as SbCarouselComponent } from '@valk-nx/storyblok/components/carousel/src/lib/carousel';
import { CarouselWithAvailabilityWidgetComponent as SbCarouselWithAvailabilityWidgetComponent } from '@valk-nx/storyblok/components/carousel-with-availability-widget/src/lib/carousel-with-availability-widget';
import { RichTextComponent } from '@valk-nx/storyblok/components/richtext/src/lib/rich-text';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import { FilterableListService } from '@valk-nx/storyblok-services/src/lib/services/filterable-list.service';
import type { ContentData } from '@valk-nx/storyblok-types/src/lib/types/content.interface';
import type { TransformedFilterableHotel } from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';
import type { ISbStoryBlok } from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

const carouselComponents = {
	carousel: SbCarouselComponent,
	carouselWithAvailabilityWidget: SbCarouselWithAvailabilityWidgetComponent,
};

const richTextComponents = {
	richText: RichTextComponent,
};

type FilterableListHotel = 'vdc' | 'vhi';
type FilterableListType = 'hotels' | 'packages' | 'events' | 'rooms';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	selector: 'sb-filterable-list',
	templateUrl: './filterable-list.html',
	imports: [
		AlertComponent,
		CommonModule,
		DynamicModule,
		FiltersComponent,
		FilterHeadingComponent,
		PersuadeCardComponent,
		RouterModule,
		TranslatePipe,
	],
})
export class FilterableList extends StoryblokRootDirective {
	protected readonly filterableListService = inject(FilterableListService);
	readonly bookingtoolUrl = inject(BOOKINGTOOL);
	private readonly activatedRoute = inject(ActivatedRoute);

	hotel = input.required<FilterableListHotel>();
	type = input.required<FilterableListType>();
	carousel = input.required<ISbStoryBlok[]>();
	content = input.required<ISbStoryBlok[]>();

	baseUrl = toSignal(
		this.activatedRoute.url.pipe(
			map(
				(segments) =>
					'/' +
					segments
						.map((s) => s.path)
						.filter(Boolean)
						.join('/'),
			),
		),
		{ initialValue: '/' },
	);

	sortOption = signal<string>('name-asc');

	hotels = computed<TransformedFilterableHotel[]>(
		() =>
			this.filterableListService.hotelList
				.value()
				?.hotels.map((hotel) => ({
					...hotel,
					content: this.transformContent(hotel.content),
				})) ?? [],
	);

	sortedHotels = computed<TransformedFilterableHotel[]>(() => {
		const hotels = [...this.hotels()];

		switch (this.sortOption()) {
			case 'name-asc':
				return hotels.sort((a, b) => a.name.localeCompare(b.name));
			case 'name-desc':
				return hotels.sort((a, b) => b.name.localeCompare(a.name));
			case 'price-asc':
				return hotels.sort((a, b) => {
					if (a.price.amount === null && b.price.amount === null)
						return 0;
					if (a.price.amount === null) return 1;
					if (b.price.amount === null) return -1;
					return a.price.amount - b.price.amount;
				});
			case 'price-desc':
				return hotels.sort((a, b) => {
					if (a.price.amount === null && b.price.amount === null)
						return 0;
					if (a.price.amount === null) return 1;
					if (b.price.amount === null) return -1;
					return b.price.amount - a.price.amount;
				});
			default:
				return hotels;
		}
	});

	activeFilterCount = computed(() =>
		Object.values(this.filterableListService.filters()).reduce(
			(sum, values) => sum + values.length,
			0,
		),
	);

	filters = computed<FiltersInterface[]>(() =>
		this.filterableListService.hotelList
			.value()
			?.filters.map(({ title, data }) => ({
				title,
				items: data.map(({ name, code, frequency }) => ({
					name,
					value: code,
					frequency,
				})),
			})),
	);

	shouldShowCarousel = computed(() => this.carousel().length > 0);

	carouselInputs = computed<(InputsType & { component: string })[]>(() =>
		this.carousel().map((blok) => ({
			...blok,
			component: blok['component'] ?? '',
		})),
	);

	contentInputs = computed<(InputsType & { component: string })[]>(() =>
		this.content().map((blok) => ({
			...blok,
			component: blok['component'] ?? '',
		})),
	);

	pricingTooltipOccupancy = PRICING_TOOLTIP_OCCUPANCY;

	carouselComponents: typeof carouselComponents = carouselComponents;
	richTextComponents: typeof richTextComponents = richTextComponents;

	constructor() {
		super();

		effect(() => {
			negativeHeader$.next(this.shouldShowCarousel());
		});
	}

	// TODO: VPL-44322 - Extract this logic to it's own helper for better reusability
	private readonly transformContent = (content: ContentData) => {
		if (content?.type === 'array') {
			return content.value.map((item) => `- ${item}`).join('\n');
		}

		return content.value as string;
	};
}
